import { z } from 'zod';

/**
 * TimeCardUserOrderRequirement - Requisito utente per timecard
 * Corrisponde al modello backend TimeCardUserOrderRequirement
 */
export interface TimeCardUserOrderRequirement {
	/** Ore lavorate */
	hours: number; // decimal serializzato come number
	/** Ore approvate */
	approvedHours: number; // decimal serializzato come number
}

/**
 * UserOrderRequirement - Requisito utente per ordine
 * Corrisponde al modello backend UserOrderRequirement
 */
export interface UserOrderRequirement {
	/** Nome utente */
	userName: string;
	/** Ore totali (calcolato: sum di TimeCardUserOrderRequirements.hours) */
	hours: number; // decimal calcolato
	/** Ore approvate totali (calcolato: sum di TimeCardUserOrderRequirements.approvedHours) */
	approvedHours: number; // decimal calcolato
	/** Progresso approvazione (calcolato: approvedHours / hours) */
	approvalProgress: number; // decimal calcolato
	// NOTA: timeCardUserOrderRequirements è [JsonIgnore] quindi non viene serializzato
}

/**
 * OrderRequirement - Requisito di un ordine
 * Corrisponde al modello backend OrderRequirement
 */
export interface OrderRequirement {
	/** ID del requisito (nullable) */
	requirementId?: number | null;
	/** Status del requisito */
	requirementStatus: number;
	/** Codice del requisito */
	requirementCode: string;
	/** Descrizione del requisito */
	requirementDescription: string;
	/** Ore budget */
	budgetHours: number; // decimal serializzato come number
	/** Ore totali (calcolato: sum di userOrderRequirements.hours) */
	hours: number; // decimal calcolato
	/** Performance (calcolato: budgetHours == 0 ? 1 : hours / budgetHours) */
	performance: number; // decimal calcolato
	/** Ore approvate totali (calcolato: sum di userOrderRequirements.approvedHours) */
	approvedHours: number; // decimal calcolato
	/** Progresso approvazione (calcolato: hours == 0 ? 0 : approvedHours / hours) */
	approvalProgress: number; // decimal calcolato
	/** Status complessivo (calcolato: budgetHours == 0 ? 1 : approvedHours / budgetHours) */
	overallStatus: number; // decimal calcolato
	/** Lista dei requisiti utente */
	userOrderRequirements: UserOrderRequirement[];
}

/**
 * OrderRequirementAnalysis - Analisi dei requisiti di un ordine
 * Corrisponde al modello backend OrderRequirementAnalysis
 */
export interface OrderRequirementAnalysis {
	/** ID dell'ordine */
	orderId: number;
	/** Codice dell'ordine */
	orderCode: string;
	/** Nome dell'ordine */
	orderName: string;
	/** Ore budget dell'ordine (nullable) */
	orderBudgetHours?: number | null; // decimal nullable serializzato come number
	/** Ore budget totali (calcolato: sum di requirements.budgetHours o orderBudgetHours) */
	budgetHours: number; // decimal calcolato
	/** Ore totali (calcolato: sum di requirements.hours) */
	hours: number; // decimal calcolato
	/** Ore approvate totali (calcolato: sum di requirements.approvedHours) */
	approvedHours: number; // decimal calcolato
	/** Performance (calcolato: budgetHours == 0 ? 1 : hours / budgetHours) */
	performance: number; // decimal calcolato
	/** Progresso approvazione (calcolato: hours == 0 ? 0 : approvedHours / hours) */
	approvalProgress: number; // decimal calcolato
	/** Status complessivo (calcolato: budgetHours == 0 ? 1 : approvedHours / budgetHours) */
	overallStatus: number; // decimal calcolato
	/** Lista dei requisiti */
	requirements: OrderRequirement[];
}

/**
 * Schema Zod per la validazione di TimeCardUserOrderRequirement
 */
export const TimeCardUserOrderRequirementSchema = z.object({
	hours: z.number(),
	approvedHours: z.number(),
});

/**
 * Schema Zod per la validazione di UserOrderRequirement
 */
export const UserOrderRequirementSchema = z.object({
	userName: z.string(),
	hours: z.number(),
	approvedHours: z.number(),
	approvalProgress: z.number(),
});

/**
 * Schema Zod per la validazione di OrderRequirement
 */
export const OrderRequirementSchema = z.object({
	requirementId: z.number().nullish(),
	requirementStatus: z.number(),
	requirementCode: z.string(),
	requirementDescription: z.string(),
	budgetHours: z.number(),
	hours: z.number(),
	performance: z.number(),
	approvedHours: z.number(),
	approvalProgress: z.number(),
	overallStatus: z.number(),
	userOrderRequirements: z.array(UserOrderRequirementSchema),
});

/**
 * Schema Zod per la validazione di OrderRequirementAnalysis
 */
export const OrderRequirementAnalysisSchema = z.object({
	orderId: z.number(),
	orderCode: z.string(),
	orderName: z.string(),
	orderBudgetHours: z.number().nullish(),
	budgetHours: z.number(),
	hours: z.number(),
	approvedHours: z.number(),
	performance: z.number(),
	approvalProgress: z.number(),
	overallStatus: z.number(),
	requirements: z.array(OrderRequirementSchema),
});
